import { parsePaymentConfirmationUserData, useBookingConfirmationSummary } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import type { LeadPayload } from '@/lib/gtm'

import { PaymentCompleteComponent } from '@/components/payment-confirmation/payment-complete'
import { PaymentFailedComponent } from '@/components/payment-confirmation/payment-failed'
import { PaymentPendingComponent } from '@/components/payment-confirmation/payment-pending'
import { useTrackLeadOnce } from '@/hooks/use-track-lead-once'

import { DepositPaymentConfirmation } from './-components/deposit-payment-confirmation'
import { InsurancePaymentConfirmation } from './-components/insurance-payment-confirmation'
import { buildPaymentConfirmationBookingDataFromSummary } from './-components/payment-confirmation-booking-data'

// bookingRef comes from either:
// - Our own query param (non-CC direct redirect)
// - Axepta's UserData returned as a query param on the redirect URL
//   UserData formats:
//     BOOKING:   "<affidiumPnr>|<sessionId>"
//     DEPOSIT:   "deposit|<reference>|<amountCents>"
//     INSURANCE: "insurance|<reference>|<bookingId>|<amountCents>|<sessionId?>"
// Data + Len are encrypted Axepta callback params appended to failure/success URLs
// session is the booking session ID passed via URL for non-CC direct redirects
const paymentConfirmationSearchSchema = z.object({
  mid: z.string().optional(),
  payId: z.string().optional(),
  transId: z.string().optional(),
  bookingRef: z.string().optional(),
  code: z.string().optional(),
  Code: z.coerce.string().optional(),
  Data: z.string().optional(),
  data: z.string().optional(),
  description: z.string().optional(),
  Description: z.string().optional(),
  Len: z.coerce.string().optional(),
  len: z.coerce.string().optional(),
  mac: z.string().optional(),
  MAC: z.string().optional(),
  MID: z.string().optional(),
  PayID: z.string().optional(),
  paymentRedirectUrl: z.string().url().optional(),
  session: z.string().optional(),
  status: z.string().optional(),
  Status: z.string().optional(),
  TransID: z.string().optional(),
  UserData: z.string().optional(),
  userData: z.string().optional(),
})

export const Route = createFileRoute('/confirmation-paiement/$status')({
  component: RouteComponent,
  validateSearch: paymentConfirmationSearchSchema,
})

export function BookingConfirmation({
  bookingRef: bookingRefFromUserData,
  resolvedAxeptaDetails,
  searchParams,
  sessionIdFromUserData,
  status,
}: {
  bookingRef?: string
  resolvedAxeptaDetails: Record<string, string> | undefined
  searchParams: z.infer<typeof paymentConfirmationSearchSchema>
  sessionIdFromUserData?: string
  status: string
}) {
  const navigate = useNavigate()
  // bookingRef: explicit param (non-CC) > parsed from UserData (CC) > undefined
  const bookingRef = searchParams.bookingRef ?? bookingRefFromUserData

  // sessionId: explicit param (non-CC direct redirect) > parsed from UserData (CC flow)
  const sessionId = searchParams.session ?? sessionIdFromUserData

  // On poll le summary jusqu'à ce qu'un paiement encaissé apparaisse (webhook
  // Axepta). En pending : pour basculer vers success dès l'encaissement. En
  // success : pour que le `purchase` (calculé sur le montant réellement encaissé)
  // parte même si la redirection navigateur précède le webhook.
  const isPending = status === 'pending'
  const isSuccess = status === 'success'
  const { data: summary } = useBookingConfirmationSummary(
    sessionId,
    isPending || isSuccess ? { pollUntilPaid: true } : undefined
  )

  useEffect(() => {
    if (!isPending) return
    if (!summary?.latestPaidPaymentId) return
    void navigate({
      params: { status: 'success' },
      replace: true,
      search: searchParams,
      to: '/confirmation-paiement/$status',
    })
  }, [isPending, summary?.latestPaidPaymentId, navigate, searchParams])

  const bookingData = useMemo(
    () => buildPaymentConfirmationBookingDataFromSummary(summary, bookingRef),
    [summary, bookingRef]
  )

  // Conversion Google Ads pour les dossiers en paiement différé (virement,
  // chèques-vacances, espèces…) : on pousse `generate_lead` (sans montant, pas
  // une vente encaissée). On exclut le SSP (`paymentRedirectUrl`), qui basculera
  // en `purchase`, et tout dossier déjà encaissé (`latestPaidPaymentId`).
  const leadPayload = useMemo<LeadPayload | null>(() => {
    if (!isPending || searchParams.paymentRedirectUrl) return null
    if (!summary || summary.latestPaidPaymentId) return null
    return { transactionId: summary.bookingReference, currency: summary.currency }
  }, [isPending, searchParams.paymentRedirectUrl, summary])
  useTrackLeadOnce(leadPayload)

  const sspRescueBanner =
    isPending && searchParams.paymentRedirectUrl ? (
      <SspRescueCard paymentRedirectUrl={searchParams.paymentRedirectUrl} />
    ) : null

  const statusComponents = {
    failed: (
      <PaymentFailedComponent
        axeptaDetails={resolvedAxeptaDetails}
        booking={bookingData}
        sessionId={sessionId}
        summary={summary}
      />
    ),
    pending: (
      <PaymentPendingComponent
        booking={bookingData}
        headerBanner={sspRescueBanner}
        sessionId={sessionId}
        summary={summary}
      />
    ),
    success: (
      <PaymentCompleteComponent booking={bookingData} sessionId={sessionId} summary={summary} />
    ),
  } as const

  return (
    statusComponents[normalizeStatus(status) as keyof typeof statusComponents] ??
    statusComponents.pending
  )
}

function buildPlainAxeptaDetails(searchParams: z.infer<typeof paymentConfirmationSearchSchema>) {
  const code = searchParams.Code ?? searchParams.code
  const description = searchParams.Description ?? searchParams.description
  const status = searchParams.Status ?? searchParams.status
  const transId = searchParams.TransID ?? searchParams.transId
  const payId = searchParams.PayID ?? searchParams.payId
  const userData = searchParams.UserData ?? searchParams.userData
  const mid = searchParams.MID ?? searchParams.mid
  const mac = searchParams.MAC ?? searchParams.mac

  if (!code && !description && !status && !transId && !payId && !userData) {
    return
  }

  return {
    ...(code && { Code: code }),
    ...(description && { Description: description }),
    ...(status && { Status: status }),
    ...(transId && { TransID: transId }),
    ...(payId && { PayID: payId }),
    ...(userData && { UserData: userData }),
    ...(mid && { MID: mid }),
    ...(mac && { MAC: mac }),
  }
}

function normalizeStatus(status: string): 'failed' | 'success' | 'unknown' {
  if (status === 'success' || status === 'failed') return status
  return 'unknown'
}

const SSP_PENDING_TIMEOUT_MS = 5 * 60 * 1000

export function RouteComponent() {
  const { status } = Route.useParams()
  const searchParams = Route.useSearch()

  // Les paramètres Axepta chiffrés (Data+Len) ne sont plus déchiffrés côté client :
  // l'endpoint public de déchiffrement était un oracle Blowfish non authentifié.
  // Le statut réel du paiement est de toute façon établi par le webhook axepta-notify
  // et récupéré via le summary de la session.
  const resolvedAxeptaDetails = buildPlainAxeptaDetails(searchParams)

  const rawUserData =
    searchParams.UserData ?? searchParams.userData ?? resolvedAxeptaDetails?.UserData
  const parsedUserData = parsePaymentConfirmationUserData(rawUserData)
  // Axepta TransID identifies the exact BookingPayment record for receipt download
  const transId = searchParams.TransID ?? searchParams.transId ?? resolvedAxeptaDetails?.TransID

  // ── LEGACY DEPOSIT fork ──────────────────────────────────────────────────
  if (parsedUserData.flow === 'legacy_deposit') {
    return (
      <DepositPaymentConfirmation
        amountCents={parsedUserData.amountCents}
        reference={parsedUserData.bookingRef}
        status={normalizeStatus(status)}
        transId={transId}
      />
    )
  }

  // ── DEPOSIT fork ──────────────────────────────────────────────────────────
  if (parsedUserData.flow === 'deposit') {
    return (
      <DepositPaymentConfirmation
        amountCents={parsedUserData.amountCents}
        reference={parsedUserData.bookingRef}
        status={normalizeStatus(status)}
        transId={transId}
      />
    )
  }

  // ── INSURANCE fork ────────────────────────────────────────────────────────
  if (parsedUserData.flow === 'insurance') {
    return (
      <InsurancePaymentConfirmation
        amountCents={parsedUserData.amountCents}
        reference={parsedUserData.bookingRef}
        status={normalizeStatus(status)}
        transId={transId}
      />
    )
  }

  // ── BOOKING fork (checkout CC + non-CC) ───────────────────────────────────
  const bookingRef = parsedUserData.flow === 'booking' ? parsedUserData.bookingRef : undefined
  const sessionIdFromUserData =
    parsedUserData.flow === 'booking' ? parsedUserData.sessionId : undefined

  return (
    <BookingConfirmation
      bookingRef={bookingRef}
      resolvedAxeptaDetails={resolvedAxeptaDetails}
      searchParams={searchParams}
      sessionIdFromUserData={sessionIdFromUserData}
      status={status}
    />
  )
}

export function SspRescueCard({ paymentRedirectUrl }: { paymentRedirectUrl: string }) {
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    const handle = window.setTimeout(() => setTimedOut(true), SSP_PENDING_TIMEOUT_MS)
    return () => window.clearTimeout(handle)
  }, [])

  const handleReopen = () => {
    window.open(paymentRedirectUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-light-200 bg-white p-6">
      <div className="text-2xl font-semibold text-gray-light-900">
        {timedOut
          ? "Nous n'avons pas encore reçu de confirmation de votre paiement"
          : 'Finalisez votre paiement dans le nouvel onglet'}
      </div>
      <p className="text-sm text-gray-light-700">
        {timedOut ? (
          <>
            Si vous avez bien validé le paiement, votre dossier sera mis à jour automatiquement dès
            réception de la confirmation. Sinon, vous pouvez rouvrir la page de paiement ci-dessous,
            ou nous contacter au{' '}
            <a className="text-brand-600 underline" href="tel:+33491041111">
              04 91 04 11 11
            </a>
            .
          </>
        ) : (
          "Votre paiement en plusieurs fois s'effectue sur la page de notre prestataire, ouverte dans un nouvel onglet. Cette page se mettra à jour automatiquement dès réception de la confirmation."
        )}
      </p>
      <div>
        <Button onClick={handleReopen} type="button" variant={timedOut ? 'default' : 'outline'}>
          <ExternalLink className="mr-2 size-4" />
          Rouvrir la page de paiement
        </Button>
      </div>
    </div>
  )
}
